import {
  COMMAND_FUNNEL_LABELS,
  JOB_STAGE_COLUMNS,
  JOB_STAGE_COLUMN_MODULES,
  REPORT_PERIOD_LABELS,
  SELECTED_STAGE_LABEL,
  formatMoney,
  type JobStageColumn,
  type ModuleState,
  type MoneyFigure,
  type OpenRequisitionRow,
  type ResolvedReportFilters,
} from '@nest-recruit/shared';
import { EMPTY, formatIsoDate } from '../../lib/format';

/** "42%", or `—` with nothing to divide by (06-qol §11). */
export function percent(share: number | null): string {
  return share === null ? EMPTY : `${Math.round(share * 100)}%`;
}

/** Q17: one figure per currency, side by side — "৳6,667 · £100" — never added together. */
export function moneyList(figures: readonly MoneyFigure[]): string {
  if (figures.length === 0) return EMPTY;
  return figures.map((figure) => formatMoney({ amountMinor: BigInt(figure.amountMinor), currency: figure.currency })).join(' · ');
}

export function days(value: number | null): string {
  return value === null ? EMPTY : `${value} ${value === 1 ? 'day' : 'days'}`;
}

export function workingDaysText(value: number | null): string {
  return value === null ? EMPTY : `${value} working ${value === 1 ? 'day' : 'days'}`;
}

/** "This month · 01-10-2026 to 02-10-2026". */
export function periodText(filters: ResolvedReportFilters): string {
  return `${REPORT_PERIOD_LABELS[filters.period]} · ${formatIsoDate(filters.from)} to ${formatIsoDate(filters.to)}`;
}

/**
 * RPT-01a's furthest stage. A request with no job made from it says so: its empty pipeline is
 * about the missing link, not about candidates. `offer` reads "Selected" while Offers is off (§L33).
 */
export function furthestStageText(row: Pick<OpenRequisitionRow, 'linkedJobs' | 'furthestStage'>, isOffersOn: boolean): string {
  if (row.linkedJobs === 0) return 'No job linked yet';
  if (row.furthestStage === null) return 'No candidates yet';
  if (row.furthestStage === 'offer' && !isOffersOn) return SELECTED_STAGE_LABEL;
  return COMMAND_FUNNEL_LABELS[row.furthestStage];
}

/** §L28: a column of a switched-off module leaves, and its neighbours close up. */
export function shownJobStageColumns(modules: ModuleState): readonly JobStageColumn[] {
  return JOB_STAGE_COLUMNS.filter((column) => {
    const module = JOB_STAGE_COLUMN_MODULES[column];
    return module === undefined || modules[module];
  });
}
